import * as ko from 'knockout';
import {ObjectTypeId} from '../../constants';
import {getHash, isRoutePermissionOkay} from '../../constants/RouteDefinitions';
import {getObjectDefinition} from '../../util/misc';
import {storeObservable} from '../../util/sessionstore';
import {IOverrideDetailsRow, IOverrideValueComparison} from './IOverrideDetailsRow';
import {
    IOverrideDetailsKeys,
    MEMBERSHIP_OVERRIDE_DETAILS_KEYS,
} from './overrideDetailsKeys';
import override_detailsHtml from './override-details.html?raw';

export const template = override_detailsHtml;

interface IParams {
    row: IOverrideDetailsRow;
    keys?: IOverrideDetailsKeys;
}

/** Expanded override audit card (heading, meta, justification, ids) for permissions / membership. */
export class viewModel {
    readonly row: IOverrideDetailsRow;
    readonly keys: IOverrideDetailsKeys;
    readonly hasJustification: KnockoutComputed<boolean>;
    /** True when the draft differs from the saved override comment. */
    readonly canSaveComment: KnockoutComputed<boolean>;
    readonly sectionHeadingKey: KnockoutComputed<string>;
    readonly valueComparison: KnockoutComputed<IOverrideValueComparison | null>;
    /** True when the actor is the application (`System`), not a person. */
    readonly isSystemActor: KnockoutComputed<boolean>;

    constructor(params: IParams) {
        this.row = params.row;
        this.keys = params.keys ?? MEMBERSHIP_OVERRIDE_DETAILS_KEYS;
        this.hasJustification = ko.pureComputed(() => !!this.commentText().trim());
        this.canSaveComment = ko.pureComputed(() => {
            return this.row.overrideCommentDraft() !== this.commentText();
        });
        this.sectionHeadingKey = ko.pureComputed(() => {
            this.trackPanelRevision();
            return this.row.getOverrideSectionHeadingKey() ?? '';
        });
        this.valueComparison = ko.pureComputed(() => {
            this.trackPanelRevision();
            return this.row.getOverrideValueComparison?.() ?? null;
        });
        this.isSystemActor = ko.pureComputed(() => {
            storeObservable('language')();
            this.trackPanelRevision();
            return this.actorRawName() === 'System';
        });
    }

    panelWhen = (): string => {
        this.trackPanelRevision();
        return this.row.isOverrideDeleted()
            ? this.row.overrideDeletedWhen()
            : this.row.overrideOverriddenWhen();
    };

    commentText = (): string => {
        this.trackPanelRevision();
        return this.row.overrideCommentText() ?? '';
    };

    actorNameText = (): string => {
        this.trackPanelRevision();
        return this.actorRawName();
    };

    /** Link when the actor is a person with an id and the user may open user details. */
    canLinkActor = (): boolean => {
        this.trackPanelRevision();
        const id = this.actorId();
        if (!id || this.actorRawName() === 'System') {
            return false;
        }
        const routeId = getObjectDefinition(ObjectTypeId.User)?.routeDetails;
        return !!routeId && isRoutePermissionOkay(routeId);
    };

    actorHref = (): string => {
        this.trackPanelRevision();
        const id = this.actorId();
        const routeId = getObjectDefinition(ObjectTypeId.User)?.routeDetails;
        if (!id || !routeId) {
            return '';
        }
        return getHash(routeId, {id}) ?? '';
    };

    overrideIdText = (): string => {
        this.trackPanelRevision();
        return this.row.overrideIdText();
    };

    overrideValueText = (): string => {
        this.trackPanelRevision();
        return this.row.overrideValueText();
    };

    overrideSyncImportFileIdText = (): string => {
        this.trackPanelRevision();
        return this.row.overrideSyncImportFileIdText();
    };

    /** Link when the override has a real import file id and the user may open import history. */
    canLinkSyncImportFileId = (): boolean => {
        this.trackPanelRevision();
        const id = this.row.getOverride()?.syncronizedimportfileid;
        return !!id && isRoutePermissionOkay('importhistorydetails');
    };

    syncImportFileHref = (): string => {
        this.trackPanelRevision();
        const id = this.row.getOverride()?.syncronizedimportfileid;
        return getHash('importhistorydetails', {importfileid: id}) ?? '';
    };

    canEditComment = (): boolean => {
        this.trackPanelRevision();
        return this.row.canEditOverrideComment();
    };

    /** Persist only when the draft actually changed. */
    submitComment = (): void => {
        if (!this.canSaveComment()) {
            return;
        }
        this.row.submitOverrideComment();
    };

    /** Subscribe panel bindings to the host revision so reconcile refreshes heading, comment, and ids together. */
    private trackPanelRevision(): void {
        this.row.overridePanelRevision();
    };

    /** Raw actor name from the override (overriddenby / deletedby). */
    private actorRawName(): string {
        return (this.row.isOverrideDeleted()
            ? this.row.overrideDeletedByName()
            : this.row.overrideOverriddenByName()) ?? '';
    }

    /** Actor user id from the override (overriddenbyid / deletedbyid). */
    private actorId(): number {
        const override = this.row.getOverride();
        if (!override) {
            return 0;
        }
        return this.row.isOverrideDeleted()
            ? override.deletedbyid
            : override.overriddenbyid;
    }

    dispose(): void {
        this.hasJustification.dispose();
        this.canSaveComment.dispose();
        this.sectionHeadingKey.dispose();
        this.valueComparison.dispose();
        this.isSystemActor.dispose();
    }
}
